import React, { useMemo } from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import { Theme } from '@app/theme';
import { Trans, useTranslation } from 'react-i18next';

import {
  useCurrentOrganizationId,
  useCurrentOrganizationInvitedMembers,
  useCurrentOrganizationMembers,
} from 'queries';
import { useBoolModalState, Button, InformationBox } from '@app/core';
import { AddMemberModal } from './components/AddMemberModal';
import { InvitedTeamMemberItem } from './components/InvitedTeamMemberItem';
import { TeamMemberItem } from './components/TeamMemberItem';
import { SettingContent } from './components/SettingContent';

const useStyles = createUseStyles((theme: Theme) => ({
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.marginBase * 3,
  },
  title: {
    ...theme.fonts.h2,
  },
  memberList: {
    listStyle: 'none',
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },
  information: {
    marginBottom: theme.marginBase * 6,
  },
}));

export const TeamInformations = () => {
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });
  const { t } = useTranslation();
  const { data: members } = useCurrentOrganizationMembers();
  const { data: invitedMembers } = useCurrentOrganizationInvitedMembers();

  const [addMemberModalOpened, openAddMemberModal, closeAddMemberModal] =
    useBoolModalState();

  const invitedMembersWithoutDuplicates = useMemo(() => {
    if (!invitedMembers) {
      return [];
    }

    const uniqueInvitedMembersEmail = new Set();

    return invitedMembers.filter((invitedMember) => {
      if (!uniqueInvitedMembersEmail.has(invitedMember.email)) {
        uniqueInvitedMembersEmail.add(invitedMember.email);
        return true;
      }
      return false;
    });
  }, [invitedMembers]);

  return (
    <SettingContent>
      <div className={classes.header}>
        <h1 className={classes.title}>
          <Trans i18nKey="teamInformations.title" />
        </h1>
        <div>
          <Button
            text={t('teamInformations.invite')}
            onClick={openAddMemberModal}
            small
            color="accent"
          />
          <AddMemberModal
            onRequestClose={closeAddMemberModal}
            isOpen={addMemberModalOpened}
          />
        </div>
      </div>

      <InformationBox
        text={t('teamInformations.inviteInfo')}
        className={classes.information}
      />

      {members && (
        <ul className={classes.memberList}>
          {members.map((member) => (
            <TeamMemberItem member={member} key={member.id} />
          ))}
        </ul>
      )}
      {invitedMembersWithoutDuplicates && (
        <ul className={classes.memberList}>
          {invitedMembersWithoutDuplicates.map((invitedMember) => (
            <InvitedTeamMemberItem
              invitedMember={invitedMember}
              key={invitedMember.id}
            />
          ))}
        </ul>
      )}
    </SettingContent>
  );
};
